import { useEffect, useState, useRef, useLayoutEffect, useCallback, Component, ErrorInfo, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import { Routes, Route, Navigate, Link } from 'react-router-dom'
import {
  useNodeStore,
  useFiberStore,
  useFolderStore,
  useUIStore,
  useAuthStore,
  useMapHistoryStore,
  useIlluminationStore,
} from './stores'
import { MapContainer } from './components/map'
import { Sidebar, DetailPanel, FiberTracePanel, CableDetailPanel } from './components/panels'
import { Toolbar, NotificationToast } from './components/ui'
import { ImportExportModal, TenantAdminModal, NetworkAssistantModal } from './components/modals'
import { SubscriptionBanner } from './components/SubscriptionBanner'
import { PublicAnnouncementsBanner } from './components/PublicAnnouncementsBanner'
import { Moon, Sun, Menu, AlertTriangle, Folder, X, FileUp, LogOut, Shield, Building2, CircleUser, ChevronDown } from 'lucide-react'
import { ApiTest } from './pages/ApiTest'
import { LoginPage } from './pages/LoginPage'
import { PlatformAdminPage } from './pages/PlatformAdminPage'
import { PricingPage } from './pages/PricingPage'
import { SupportPage } from './pages/SupportPage'
import { AboutPage } from './pages/AboutPage'
import { authService } from './services/authService'
import { hasPermission, canImportExport } from './utils/permissions'
import { filterNodesByFolderView, isUnfiledFolderView } from './constants/folderFilter'

/**
 * Menú de usuario compacto (dropdown). En móvil es el único punto de acceso a
 * Cerrar sesión y tema; en desktop se muestra en paralelo con los botones
 * inline por costumbre.
 */
function UserMenu({
  user,
  theme,
  onToggleTheme,
  onLogout,
  onOpenTenantAdmin,
  showTenantAdmin,
  isSuperAdmin,
}: {
  user: { name?: string; email?: string; role?: string } | undefined | null
  theme: 'light' | 'dark'
  onToggleTheme: () => void
  onLogout: () => void
  onOpenTenantAdmin: () => void
  showTenantAdmin: boolean
  isSuperAdmin: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [panelRect, setPanelRect] = useState({ top: 0, left: 0, width: 256 })

  const updatePanelPosition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const width = Math.min(280, window.innerWidth - 16)
    let left = r.right - width
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    const top = r.bottom + 6
    setPanelRect({ top, left, width })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updatePanelPosition()
  }, [open, updatePanelPosition])

  useEffect(() => {
    if (!open) return
    const onScroll = () => updatePanelPosition()
    const onResize = () => updatePanelPosition()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, updatePanelPosition])

  const shortName = user?.name ? user.name.split(' ')[0] : 'Cuenta'

  const panel = open
    ? createPortal(
        <>
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[9998] bg-black/15 dark:bg-black/30"
          />
          <div
            role="menu"
            className="fixed z-[9999] rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-2xl"
            style={{ top: panelRect.top, left: panelRect.left, width: panelRect.width }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-gray-100 dark:border-dark-700">
              <p className="text-sm font-semibold truncate">{user?.name || 'Usuario'}</p>
              {user?.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              )}
              {user?.role && (
                <p className="mt-1 inline-block text-[10px] font-medium px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                  {user.role}
                </p>
              )}
            </div>
            <div className="py-1">
              <button
                type="button"
                onClick={() => onToggleTheme()}
                className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                {theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
              </button>
              {showTenantAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    onOpenTenantAdmin()
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
                >
                  <Building2 className="w-4 h-4" />
                  Administrar organización
                </button>
              )}
              {isSuperAdmin && (
                <Link
                  to="/platform"
                  onClick={() => setOpen(false)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm hover:bg-gray-50 dark:hover:bg-dark-700 text-left"
                >
                  <Shield className="w-4 h-4" />
                  Panel de plataforma
                </Link>
              )}
              <hr className="my-1 border-gray-100 dark:border-dark-700" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onLogout()
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left font-medium"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          </div>
        </>,
        document.body
      )
    : null

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menú de cuenta"
        className="inline-flex items-center gap-1.5 px-2 md:px-3 py-1.5 min-h-[40px] rounded-lg border border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 touch-manipulation"
        title="Mi cuenta"
      >
        <CircleUser className="w-5 h-5 shrink-0" />
        <span className="hidden md:inline text-sm font-medium max-w-[120px] truncate">
          {shortName}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {panel}
    </div>
  )
}

// Error Boundary para capturar errores de React
interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error capturado por ErrorBoundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-4 text-red-600">
              <AlertTriangle className="w-8 h-8" />
              <h1 className="text-xl font-bold">Error en la aplicación</h1>
            </div>
            <p className="text-gray-600 mb-4">
              Ocurrió un error inesperado. Por favor, recarga la página.
            </p>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-40 mb-4">
              {this.state.error?.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Recargar página
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function PlatformAdminGate() {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 text-gray-700 dark:text-gray-200">
        Cargando sesión…
      </div>
    )
  }
  if (user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />
  }
  return <PlatformAdminPage />
}

function AuthGate({ children }: { children: ReactNode }) {
  const init = useAuthStore((s) => s.init)
  const loading = useAuthStore((s) => s.loading)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    void init()
  }, [init])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 text-gray-700 dark:text-gray-200">
        Cargando sesión…
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  return <>{children}</>
}

function App() {
  const [showApiTest, setShowApiTest] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setShowApiTest(params.get('test') === 'api')
  }, [])

  if (showApiTest) {
    return <ApiTest />
  }

  return (
    <Routes>
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/soporte" element={<SupportPage />} />
      <Route
        path="/platform"
        element={
          <AuthGate>
            <PlatformAdminGate />
          </AuthGate>
        }
      />
      <Route
        path="/*"
        element={
          <AuthGate>
            <MainApp />
          </AuthGate>
        }
      />
    </Routes>
  )
}

function MainApp() {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)

  const theme = useUIStore((state) => state.theme)
  const toggleTheme = useUIStore((state) => state.toggleTheme)
  const sidebarOpen = useUIStore((state) => state.sidebarOpen)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const detailPanelOpen = useUIStore((state) => state.detailPanelOpen)
  const tracePanelOpen = useUIStore((state) => state.tracePanelOpen)
  const setTracePanelOpen = useUIStore((state) => state.setTracePanelOpen)
  const importExportModalOpen = useUIStore((state) => state.importExportModalOpen)
  const setImportExportModalOpen = useUIStore((state) => state.setImportExportModalOpen)
  const tenantAdminModalOpen = useUIStore((state) => state.tenantAdminModalOpen)
  const setTenantAdminModalOpen = useUIStore((state) => state.setTenantAdminModalOpen)
  const [impTick, setImpTick] = useState(0)
  const [networkAssistantOpen, setNetworkAssistantOpen] = useState(false)
  const [impInput, setImpInput] = useState(() => authService.getImpersonateSlug() || '')

  const showImportExport = canImportExport(
    user?.role,
    user?.permissions,
    user?.tenant?.advancedConfig ?? true
  )
  const showTenantAdmin =
    hasPermission(user?.role, 'tenant:read', user?.permissions) && !user?.tenant?.isSystem
  const selectedNodeId = useNodeStore((state) => state.selectedNodeId)
  const selectedCableId = useFiberStore((state) => state.selectedCableId)
  const setDetailPanelOpen = useUIStore((state) => state.setDetailPanelOpen)
  const editModeEnabled = useUIStore((state) => state.editModeEnabled)

  const loadNodes = useNodeStore((state) => state.loadNodes)
  const fiberInit = useFiberStore((state) => state.init)
  const folderInit = useFolderStore((state) => state.init)
  const activeFolderId = useFolderStore((state) => state.activeFolderId)
  const activeFolder = useFolderStore((state) => state.getActiveFolder())
  const setActiveFolder = useFolderStore((state) => state.setActiveFolder)
  const allNodes = useNodeStore((state) => state.nodes)
  
  // Calcular nodos filtrados para mostrar en el header
  const filteredNodesCount = filterNodesByFolderView(allNodes, activeFolderId).length

  const [mapInstance, setMapInstance] = useState<L.Map | null>(null)

  useEffect(() => {
    useNodeStore.getState().selectNode(null)
    useFiberStore.getState().selectCable(null)
    useFiberStore.getState().clearTrace()
    useIlluminationStore.getState().clearIllumination()
    useUIStore.getState().setDetailPanelOpen(false)
    useUIStore.getState().setTracePanelOpen(false)
    useUIStore.getState().clearMapSelection()
    useUIStore.getState().setMapSelectMode('navigate')
  }, [activeFolderId, impTick])

  useEffect(() => {
    const loadData = async () => {
      if (user?.role === 'SUPER_ADMIN' && !authService.getImpersonateSlug()) {
        return
      }
      try {
        await Promise.all([loadNodes(), fiberInit(), folderInit()])
        console.log('✅ Datos cargados')
      } catch (error) {
        console.error('❌ Error cargando datos:', error)
      }
    }

    void loadData()
  }, [loadNodes, fiberInit, folderInit, user?.role, impTick])

  useEffect(() => {
    // Aplicar tema
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  // Abrir panel de detalles cuando se selecciona un nodo
  useEffect(() => {
    if (selectedNodeId) {
      setDetailPanelOpen(true)
    }
  }, [selectedNodeId, setDetailPanelOpen])

  // Deshacer / rehecer movimiento de nodos en el mapa (Ctrl+Z / Ctrl+Y o Ctrl+Shift+Z)
  useEffect(() => {
    const isTypingContext = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false
      const tag = target.tagName
      return (
        target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      )
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      if (isTypingContext(e.target)) return

      const key = e.key.toLowerCase()
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault()
        void useMapHistoryStore
          .getState()
          .undo()
          .then((ok) => {
            useUIStore
              .getState()
              .addNotification(
                ok ? 'info' : 'warning',
                ok ? 'Movimiento de nodo deshecho' : 'No hay movimiento de nodo que deshacer'
              )
          })
        return
      }
      if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault()
        void useMapHistoryStore
          .getState()
          .redo()
          .then((ok) => {
            useUIStore
              .getState()
              .addNotification(
                ok ? 'info' : 'warning',
                ok ? 'Movimiento de nodo rehecho' : 'No hay movimiento de nodo que rehecer'
              )
          })
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="h-screen w-full bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-gray-100 flex flex-col">
      <PublicAnnouncementsBanner />
      <SubscriptionBanner user={user} />
      {/* Header */}
      <header
        className="h-14 md:h-16 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 flex items-center px-2 sm:px-6 z-40 min-h-[3.5rem] md:min-h-[4rem] overflow-x-auto overflow-y-visible"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-2 md:gap-3">
          {!sidebarOpen && (
            <button
              onClick={toggleSidebar}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors touch-manipulation"
              title="Abrir panel lateral"
              aria-label="Abrir panel lateral"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <div className="text-xl md:text-2xl">🌐</div>
          <div>
            <h1 className="text-base md:text-xl font-bold">NetEasyFTTX</h1>
            <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
              Sistema de Gestión de Redes FTTH
            </p>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-4 flex-nowrap sm:flex-wrap justify-end shrink-0">
          {user?.role === 'SUPER_ADMIN' && (
            <div className="flex items-center gap-1 text-xs max-w-full">
              <span className="text-gray-500 hidden lg:inline">Org (mapa):</span>
              <input
                type="text"
                value={impInput}
                onChange={(e) => setImpInput(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="slug"
                className="w-28 lg:w-36 px-2 py-1 rounded border border-gray-300 dark:border-dark-600 dark:bg-dark-700"
              />
              <button
                type="button"
                onClick={() => {
                  authService.setImpersonateSlug(impInput.trim() || null)
                  setImpTick((t) => t + 1)
                }}
                className="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Aplicar
              </button>
            </div>
          )}

          <Toolbar
            mapInstance={mapInstance}
            showNetworkAssistant={!!user?.tenant?.aiAssistant}
            onNetworkAssistant={() => setNetworkAssistantOpen(true)}
          />

          {/* Indicador de Carpeta/Proyecto Activo */}
          {activeFolder || isUnfiledFolderView(activeFolderId) ? (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                isUnfiledFolderView(activeFolderId)
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
              }`}
            >
              <Folder className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {isUnfiledFolderView(activeFolderId) ? 'Sin carpeta' : activeFolder!.name}
              </span>
              <span className="text-xs text-blue-500">
                ({filteredNodesCount} nodos)
              </span>
              <button
                onClick={() => setActiveFolder(null)}
                className="ml-1 p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded"
                title="Ver toda la organización"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Nodos:</span>
              <span className="font-bold text-primary-600 dark:text-primary-400">
                {filteredNodesCount}
              </span>
            </div>
          )}

          {showImportExport && (
            <>
              <button
                type="button"
                onClick={() => setImportExportModalOpen(true)}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 shadow-sm"
                title="KML, KMZ, CSV, imagen del mapa y reportes"
              >
                <FileUp className="w-4 h-4 shrink-0" />
                Importar / Exportar
              </button>
              <button
                type="button"
                onClick={() => setImportExportModalOpen(true)}
                className="sm:hidden p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                title="Importar / Exportar (KML, CSV…)"
              >
                <FileUp className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Menú de cuenta (única vía de Cerrar sesión, tema y admin) */}
          <UserMenu
            user={user}
            theme={theme}
            onToggleTheme={toggleTheme}
            onLogout={() => logout()}
            onOpenTenantAdmin={() => setTenantAdminModalOpen(true)}
            showTenantAdmin={showTenantAdmin}
            isSuperAdmin={user?.role === 'SUPER_ADMIN'}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden relative">
        {/* Sidebar */}
        {sidebarOpen && <Sidebar />}

        {/* Backdrop móvil cuando Sidebar abierto */}
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Cerrar panel lateral"
            onClick={toggleSidebar}
            className="md:hidden fixed inset-0 z-[1100] bg-black/40"
          />
        )}

        {/* Map Area */}
        <div className={`flex-1 relative ${editModeEnabled ? 'edit-mode-active' : ''}`}>
          {user?.role === 'SUPER_ADMIN' && !authService.getImpersonateSlug() && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1100] bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 px-4 py-2 rounded-lg shadow text-sm max-w-md text-center">
              Indica el <strong>slug</strong> de la organización y pulsa <strong>Aplicar</strong> en la barra superior para
              cargar el mapa.
            </div>
          )}
          <MapContainer onMapReady={setMapInstance} />

          {/* Indicador de modo edición */}
          {editModeEnabled && (
            <div className="edit-mode-indicator">
              ✋ Modo Mover Nodos Activo - Arrastra los nodos para moverlos
            </div>
          )}
        </div>

        {/* Detail Panel (nodos) */}
        {detailPanelOpen && !selectedCableId && (
          <div className="md:contents fixed inset-0 z-[1200] md:static md:z-auto">
            <button
              type="button"
              aria-label="Cerrar detalle"
              onClick={() => setDetailPanelOpen(false)}
              className="md:hidden absolute inset-0 bg-black/40"
            />
            <div className="md:contents absolute right-0 top-0 h-full w-[90vw] max-w-md md:w-auto md:max-w-none">
              <DetailPanel />
            </div>
          </div>
        )}

        {/* Cable Detail Panel */}
        {selectedCableId && (
          <div className="md:contents fixed inset-0 z-[1200] md:static md:z-auto">
            <button
              type="button"
              aria-label="Cerrar detalle de cable"
              onClick={() => useFiberStore.getState().selectCable(null)}
              className="md:hidden absolute inset-0 bg-black/40"
            />
            <div className="md:contents absolute right-0 top-0 h-full w-[90vw] max-w-md md:w-auto md:max-w-none">
              <CableDetailPanel />
            </div>
          </div>
        )}

        {/* Fiber Trace Panel */}
        {tracePanelOpen && (
          <FiberTracePanel
            isOpen={tracePanelOpen}
            onClose={() => setTracePanelOpen(false)}
          />
        )}
      </main>

      {/* Notificaciones Toast */}
      <NotificationToast />

      {/* Modal Import/Export */}
      <ImportExportModal
        isOpen={importExportModalOpen}
        onClose={() => setImportExportModalOpen(false)}
        mapContainer={mapInstance?.getContainer()}
      />

      <TenantAdminModal isOpen={tenantAdminModalOpen} onClose={() => setTenantAdminModalOpen(false)} />
      <NetworkAssistantModal
        isOpen={networkAssistantOpen}
        onClose={() => setNetworkAssistantOpen(false)}
      />
    </div>
  )
}

// Exportar con ErrorBoundary
export default function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  )
}
